// THE STATUS WORDS — how a thing stands, said one way wherever it is said: on
// a pill (StatusPill.js), in a filter's choices, in an inspector's line. Each
// kind of thing has its own word for a state (an app is Connected where a
// contract is Active); the tone belongs to the state, whatever the word:
//   well  — all is well (the layer's hue)
//   warn  — it wants you (paused, expired, needs setup)
//   live  — it failed, or was refused
//   quiet — over, or only said for the record
export const STATUS_WORDS = {
    app: { active: 'Connected', paused: 'Paused', expired: 'Contract expired' },
    // a plugin's lifecycle in a space, as the server answers it
    // (plugin-lifecycle.md §3), said the three ways a person sees it: Not set
    // up (in the store), Setting up (every step still open, turning it on
    // included; its next step is said beside it), Ready, and Off (paused).
    plugin: {
        not_installed: 'Not set up', not_set_up: 'Setting up', needs_connection: 'Setting up', needs_credentials: 'Setting up',
        needs_parameters: 'Setting up', needs_approval: 'Setting up', ready: 'Ready', paused: 'Off',
    },
    // how a tool runs automatically (its one timetable; the server's `schedule`):
    // On or Off, and when it's On but stuck, Can't run (something must change
    // first: the server's `paused`, `needs_realm`) or Keeps failing (it ran, and
    // the plugin's service failed). plugin-words.md
    schedule: { off: 'Off', running: 'On', paused: 'Can’t run', blocked: 'Can’t run', failing: 'Keeps failing', needs_realm: 'Can’t run' },
    // an event or manual automation (On · Off · Keeps failing); `running` and
    // `paused` are an older client's names for On and Off
    automation: { on: 'On', off: 'Off', failing: 'Keeps failing', running: 'On', paused: 'Off' },
    contract: { active: 'Active', paused: 'Paused', waiting: 'Waiting', expired: 'Expired', revoked: 'Revoked', expiring: 'Expires soon' },
    // a run's outcome (plugin-lifecycle.md §1), and the raw statuses an older
    // feed sends, said as the outcome they are
    run: {
        done: 'Done', partial: 'Partial', failed: 'Failed', refused: 'Refused', waiting: 'Waiting', skipped: 'Skipped',
        cancelled: 'Cancelled', unknown: 'Unknown', running: 'Running',
        success: 'Done', completed: 'Done', error: 'Failed', pending: 'Waiting', queued: 'Waiting', dispatched: 'Waiting', parked: 'Waiting',
    },
};

const TONE = {
    active: 'well', running: 'well', success: 'well', completed: 'well', ready: 'well', on: 'well', done: 'well',
    paused: 'warn', blocked: 'warn', partial: 'warn', parked: 'warn', expired: 'warn', pending: 'warn', queued: 'warn', dispatched: 'warn', waiting: 'warn',
    not_set_up: 'warn', needs_connection: 'warn', needs_credentials: 'warn', needs_parameters: 'warn', needs_approval: 'warn', needs_realm: 'warn',
    failed: 'live', error: 'live', refused: 'live', failing: 'live',
    revoked: 'quiet', skipped: 'quiet', unknown: 'quiet', expiring: 'quiet', not_installed: 'quiet', off: 'quiet', cancelled: 'quiet',
};

/** The word for a state, as `of` (app, plugin, schedule, automation, contract, run) says it; the state itself when it has none. */
export function statusWord(status, of) {
    return STATUS_WORDS[of]?.[status] || status;
}

/** A state's tone: well, warn, live or quiet. */
export function statusTone(status) {
    return TONE[status] || 'quiet';
}

// WHY A TOOL CAN'T RUN AUTOMATICALLY (the server's `paused`): the scheduler's
// reasons, in its order, then the stamps it leaves on one it switched off.
// `cannotWrite` names what a realm no longer lets the tool write.
const SCHEDULE_REASONS = {
    not_approved: 'Its plugin isn’t turned on',
    per_run_consent: 'It asks you every run, so it can’t run automatically',
    not_connected: 'Its plugin isn’t signed in',
    not_configured: 'A setting it needs has no value',
    realm_revoked: 'The realm it runs in no longer lets it run',
    needs_realm: 'Choose a realm it runs in',
    tool_missing: 'Its tool is gone',
    plugin_paused: 'Its plugin is off',
    merged_into_tool_schedule: 'Merged into how this tool runs automatically',
};

export function scheduleReason(reason, cannotWrite = null) {
    if (!reason) return null;
    if (reason === 'realm_cannot_write') {
        const what = (cannotWrite || []).join(', ');
        return what ? `Its realm does not let it ${what}` : 'Its realm does not let it write what it writes';
    }
    return SCHEDULE_REASONS[reason] || String(reason).replace(/_/g, ' ');
}
